import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Spinner, Select } from '@shopify/polaris';
import { SortAscendingIcon, SortDescendingIcon, SearchIcon } from '@shopify/polaris-icons';
import { useAuth, useAuthFetch } from './AuthComponent';
import { useStore } from './StoreContext';

// Inventory list memo (franchise request via Eric, 2026-10-08): the list
// scrolls inside .page-content (not the window), so the browser never
// restores the position when a user comes back from a product page. On a
// row click we stash the scroll offset + filter + sort in sessionStorage,
// keyed by store and emulated location, and put them back once the list has
// loaded. The memo is consumed on restore and expires after 30 minutes.
const MEMO_TTL_MS = 30 * 60 * 1000;
const memoKey = (store, locId) => `inv-list:${store}:${locId || 'own'}`;
function readMemo(key) {
    try {
        const m = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!m || typeof m !== 'object') return null;
        if (Date.now() - (m.at || 0) > MEMO_TTL_MS) { sessionStorage.removeItem(key); return null; }
        return m;
    } catch { return null; }
}
function writeMemo(key, m) {
    try { sessionStorage.setItem(key, JSON.stringify({ ...m, at: Date.now() })); } catch { /* storage blocked: no memo */ }
}
function clearMemo(key) { try { sessionStorage.removeItem(key); } catch { /* ignore */ } }
const SORT_KEYS = ['name', 'sku', 'committed', 'available', 'on_hand', 'incoming'];

function invClass(qty) {
    if (qty === 0) return 'badge badge--inv-empty';
    if (qty <= 3)  return 'badge badge--inv-warn';
    return 'badge badge--inv-ok';
}

function InvBadge({ value, warn }) {
    const cls = (warn === false) ? 'badge badge--inv-ok' : invClass(value);
    return <span className={cls}>{value}</span>;
}

function SortableTh({ field, label, align = 'left', sortBy, sortDir, onSort }) {
    const active = sortBy === field;
    const Icon = sortDir === 'asc' ? SortAscendingIcon : SortDescendingIcon;
    return (
        <th
            onClick={() => onSort(field)}
            className={align === 'center' ? 'col-center th-sortable' : 'th-sortable'}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title={`Sort by ${label}`}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {label}
                {active
                    ? <Icon width={12} height={12} />
                    : <span style={{ width: 12, height: 12, display: 'inline-block', opacity: 0.25 }}>↕</span>}
            </span>
        </th>
    );
}

export default function Products() {
    const navigate = useNavigate();
    const { emulating } = useAuth();
    const { activeStore } = useStore();
    const storeFetch = useAuthFetch();
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [location, setLocation] = useState(null);
    const key = memoKey(activeStore, emulating?.id);
    const restoreRef = useRef(readMemo(key));          // pending restore, consumed once applied
    const memo = restoreRef.current;
    const [search, setSearch]     = useState(memo?.search || '');
    // Club filter (Eric 2026-10-14): the location's clubs (same list as the Orders page Club filter);
    // a product matches when it carries the tag `club: <name>`. Hidden when the location has no clubs.
    const [club, setClub]         = useState(memo?.club || '');
    const [clubs, setClubs]       = useState([]);
    const [sortBy, setSortBy]     = useState(SORT_KEYS.includes(memo?.sortBy) ? memo.sortBy : 'name');   // 'name' | 'sku' | 'committed' | 'available' | 'on_hand' | 'incoming'
    const [sortDir, setSortDir]   = useState(memo?.sortDir === 'desc' ? 'desc' : 'asc');    // 'asc' | 'desc'
    const [streamDone, setStreamDone] = useState(false);
    const [returnedPid, setReturnedPid] = useState(null);  // row to flash after a restore
    const [settled, setSettled] = useState(!memo);        // false while a restore is pending: the list is laid out but not shown
    const pageHeaderRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const filterBarRef  = useRef(null);
    const [filterBarH, setFilterBarH]     = useState(56);

    useEffect(() => {
        const el = pageHeaderRef.current;
        if (!el) return;
        // Fractional height: offsetHeight rounds (71.594 → 72) and the
        // sub-pixel slit let the table show through above the filter bar.
        const h = () => el.getBoundingClientRect().height;
        const ro = new ResizeObserver(() => setHeaderHeight(h()));
        ro.observe(el);
        setHeaderHeight(h());
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const el = filterBarRef.current;
        if (!el) return;
        const h = () => el.getBoundingClientRect().height;
        const ro = new ResizeObserver(() => setFilterBarH(h()));
        ro.observe(el);
        setFilterBarH(h());
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        storeFetch('/api/products/clubs')
            .then(r => (r.ok ? r.json() : { clubs: [] }))
            .then(d => { if (!cancelled) setClubs(Array.isArray(d.clubs) ? d.clubs : []); })
            .catch(() => { if (!cancelled) setClubs([]); });
        return () => { cancelled = true; };
    }, [storeFetch, emulating?.id, activeStore]);

    useEffect(() => {
        // A remembered club that this location no longer lists falls back to "All clubs".
        if (club && clubs.length && !clubs.some(c => c.toLowerCase() === club.toLowerCase())) setClub('');
    }, [clubs, club]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setAllProducts([]);
        setStreamDone(false);
        const abort = new AbortController();
        let cancelled = false;

        async function streamPages() {
            let cursor = null;
            let firstPage = true;
            while (!cancelled) {
                const url = cursor
                    ? `/api/products?cursor=${encodeURIComponent(cursor)}`
                    : `/api/products`;
                let data;
                try {
                    const res = await storeFetch(url, { signal: abort.signal });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    data = await res.json();
                } catch (err) {
                    if (err.name === 'AbortError') return;
                    if (!cancelled) setError(err.message || 'Failed to load');
                    return;
                }
                if (cancelled) return;
                if (firstPage) {
                    setLocation(data.location || null);
                    setLoading(false);
                    firstPage = false;
                }
                if (data.products && data.products.length) {
                    setAllProducts(prev => [...prev, ...data.products]);
                }
                if (!data.has_more || !data.next_cursor) { setStreamDone(true); return; }
                cursor = data.next_cursor;
            }
        }

        streamPages().finally(() => { if (!cancelled) setStreamDone(true); });
        return () => { cancelled = true; abort.abort(); };
    }, [emulating?.id]); // eslint-disable-line

    // Put the list back where it was. The row we left from is the anchor:
    // while pages are still streaming in, rows sorted above it keep landing
    // and would push it down the screen, so after every batch the pane is
    // re-scrolled (before paint) to keep that row at the same place on
    // screen as when it was clicked. Falls back to the raw offset when the
    // product is gone. The memo is consumed once the stream has finished.
    useLayoutEffect(() => {
        const m = restoreRef.current;
        if (!m || loading) return;
        const pane = document.querySelector('.page-content');
        if (!pane) return;
        const rowBack = m.pid && document.querySelector(`.inventory-page [data-pid="${CSS.escape(m.pid)}"]`);
        if (rowBack) {
            const want = typeof m.rowTop === 'number' ? m.rowTop : 0;
            const have = rowBack.getBoundingClientRect().top - pane.getBoundingClientRect().top;
            if (Math.abs(have - want) > 1) pane.scrollTop += have - want;
            if (!m.flashed) {
                m.flashed = true;
                setSettled(true);
                setReturnedPid(m.pid);
                setTimeout(() => setReturnedPid(null), 2500);
            }
            if (!streamDone) return;           // keep anchoring until every page is in
        } else {
            if (!streamDone) return;           // not on the page yet
            if (typeof m.scrollTop === 'number') pane.scrollTop = m.scrollTop;
            setSettled(true);
        }
        restoreRef.current = null;
        clearMemo(key);
    }, [allProducts, loading, streamDone, key]);

    const openProduct = (pid) => {
        const pane = document.querySelector('.page-content');
        const row = document.querySelector(`.inventory-page [data-pid="${CSS.escape(pid)}"]`);
        const rowTop = (row && pane) ? row.getBoundingClientRect().top - pane.getBoundingClientRect().top : null;
        writeMemo(key, { scrollTop: pane ? pane.scrollTop : 0, rowTop, search, club, sortBy, sortDir, pid });
        navigate(`/store/${activeStore}/products/${String(pid).split('/').pop()}`);   // bare numeric id
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            // Text columns default to ascending (A→Z); numeric columns default
            // to descending (highest first) since that's almost always the
            // useful first view.
            setSortDir((field === 'name' || field === 'sku') ? 'asc' : 'desc');
        }
    };

    const sumField = (p, field) => p.variants.reduce((s, v) => s + (v[field] || 0), 0);
    const minSku   = (p) => {
        const skus = p.variants.map(v => v.sku || '').filter(Boolean);
        return skus.length ? skus.slice().sort()[0] : '';
    };

    const sortFns = {
        name:      (a, b) => a.title.localeCompare(b.title),
        sku:       (a, b) => minSku(a).localeCompare(minSku(b)),
        committed: (a, b) => sumField(a, 'committed') - sumField(b, 'committed'),
        available: (a, b) => sumField(a, 'available') - sumField(b, 'available'),
        on_hand:   (a, b) => sumField(a, 'on_hand')   - sumField(b, 'on_hand'),
        incoming:  (a, b) => sumField(a, 'incoming')  - sumField(b, 'incoming'),
    };

    const displayed = (() => {
        let list = search.trim()
            ? allProducts.filter(p =>
                p.title.toLowerCase().includes(search.toLowerCase()) ||
                p.variants.some(v => (v.sku || '').toLowerCase().includes(search.toLowerCase()))
              )
            : [...allProducts];
        if (club) {
            const want = club.toLowerCase();
            list = list.filter(p => (p.clubs || []).some(c => String(c).toLowerCase() === want));
        }
        const cmp = sortFns[sortBy] || sortFns.name;
        list.sort((a, b) => sortDir === 'asc' ? cmp(a, b) : -cmp(a, b));
        return list;
    })();

    return (
        <div className="inventory-page">
            {/* Page title — sticky at top of scroll container */}
            <div ref={pageHeaderRef} className="sticky-page-header">
                <h1 className="page-title">Inventory</h1>
                {location && (
                    <div className="page-subtitle">{location.location_name}</div>
                )}
            </div>

            {/* Sticky filter bar — sticks just below the page title */}
            <div ref={filterBarRef} className="products-filter-bar" style={{ top: headerHeight }}>
                <div className="products-filter-bar__search">
                    <TextField
                        placeholder="Filter by product name or SKU…"
                        value={search}
                        onChange={setSearch}
                        prefix={<SearchIcon width={16} height={16} />}
                        clearButton
                        onClearButtonClick={() => setSearch('')}
                        autoComplete="off"
                    />
                </div>
                {clubs.length > 0 && (
                    <div className="products-filter-bar__club" style={{ minWidth: 220 }}>
                        <Select
                            label="Club"
                            labelHidden
                            options={[{ label: 'All clubs', value: '' }, ...clubs.map(c => ({ label: c, value: c }))]}
                            value={club}
                            onChange={setClub}
                        />
                    </div>
                )}

                <div className="filter-bar__right products-filter-count">
                    {loading && <Spinner size="small" />}
                    {!loading && (
                        <span className="products-count-text">
                            {displayed.length} product{displayed.length !== 1 ? 's' : ''}{club ? ` in ${club}` : ''}{search ? ` matching "${search}"` : ''}
                        </span>
                    )}
                </div>
            </div>

            {error && (
                <div className="error-msg error-msg--banner">
                    Failed to load products: {error}
                </div>
            )}

            {loading && allProducts.length === 0 ? (
                <div className="empty-state">Loading inventory…</div>
            ) : displayed.length === 0 ? (
                <div className="empty-state">
                    No products found{search ? ` matching "${search}"` : ''}.
                </div>
            ) : (
                <div className="card card--no-pad" style={settled ? undefined : { visibility: 'hidden' }}>
                    {!settled && <div className="inventory-restoring" style={{ visibility: 'visible' }}>Loading inventory…</div>}
                    <table className="data-table">
                        <colgroup>
                            <col style={{ width: 100 }} />  {/* image */}
                            <col style={{ width: '22%' }} /> {/* product name */}
                            <col style={{ width: '14%' }} /> {/* SKU */}
                            <col />                          {/* variant */}
                            <col style={{ width: 90 }} />   {/* committed */}
                            <col style={{ width: 90 }} />   {/* available */}
                            <col style={{ width: 90 }} />   {/* on hand */}
                            <col style={{ width: 90 }} />   {/* incoming */}
                            <col style={{ width: 32 }} />   {/* arrow */}
                        </colgroup>
                        <thead style={{ top: headerHeight + filterBarH }}>
                            <tr>
                                <th></th>
                                <SortableTh field="name"      label="Product"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <SortableTh field="sku"       label="SKU"       sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <th>Variant</th>
                                <SortableTh field="committed" label="Committed" align="center" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <SortableTh field="available" label="Available" align="center" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <SortableTh field="on_hand"   label="On Hand"   align="center" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <SortableTh field="incoming"  label="Incoming"  align="center" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayed.map((p, pi) => {
                                const isLast = pi === displayed.length - 1;
                                return p.variants.map((v, vi) => {
                                    const isFirstVariant = vi === 0;
                                    const isLastVariant  = vi === p.variants.length - 1;
                                    const rowBorder = (!isLastVariant)
                                        ? '1px solid var(--color-border-light)'
                                        : isLast ? 'none' : '1px solid var(--color-border)';
                                    return (
                                        <tr
                                            key={v.id}
                                            onClick={() => openProduct(p.id)}
                                            className={returnedPid === p.id ? 'products-row--returned' : undefined}
                                            style={{ borderBottom: rowBorder, cursor: 'pointer' }}
                                            onMouseEnter={e => { Array.from(e.currentTarget.parentElement.querySelectorAll(`[data-pid="${p.id}"]`)).forEach(r => { r.querySelectorAll('td').forEach(td => { td.style.backgroundColor = '#f9fafb'; }); }); }}
                                            onMouseLeave={e => { Array.from(e.currentTarget.parentElement.querySelectorAll(`[data-pid="${p.id}"]`)).forEach(r => { r.querySelectorAll('td').forEach(td => { td.style.backgroundColor = ''; }); }); }}
                                            data-pid={p.id}
                                        >
                                            {isFirstVariant && (
                                                <>
                                                    {/* Image cell — spans all variants */}
                                                    <td rowSpan={p.variants.length} className="products-img-cell" style={{ borderBottom: isLast ? 'none' : '1px solid var(--color-border)' }}>
                                                        <div className="products-img-wrap" style={{ height: Math.max(60, p.variants.length * 44) }}>
                                                            {p.image
                                                                ? <img src={p.image} alt="" className="products-img" />
                                                                : <span className="products-img-placeholder">📦</span>}
                                                        </div>
                                                    </td>
                                                    {/* Product name cell — spans all variants */}
                                                    <td rowSpan={p.variants.length} className="products-title-cell" style={{ borderBottom: isLast ? 'none' : '1px solid var(--color-border)' }}>
                                                        {p.title}
                                                    </td>
                                                </>
                                            )}
                                            <td className="col-mono">{v.sku || '—'}</td>
                                            <td className="products-variant-cell">{v.title === 'Default Title' ? '—' : v.title}</td>
                                            <td className="col-center"><InvBadge value={v.committed} /></td>
                                            <td className="col-center"><InvBadge value={v.available} warn /></td>
                                            <td className="col-center"><InvBadge value={v.on_hand} /></td>
                                            <td className="col-center"><InvBadge value={v.incoming} /></td>
                                            {isFirstVariant && (
                                                <td rowSpan={p.variants.length} className="products-arrow-cell" style={{ borderBottom: isLast ? 'none' : '1px solid var(--color-border)' }}>›</td>
                                            )}
                                        </tr>
                                    );
                                });
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
